/**
 * The route tree.
 *
 * React Router in SPA mode, not framework mode. ux.md §1 selected framework mode on Workers; ADR
 * 0002 then moved apps/web to Cloudflare Pages with NO Pages Functions, which leaves no server to
 * run loaders on. SPA mode is what that decision implies, and saying so here is cheaper than
 * someone later reading ux.md and wondering what happened. Server-side logic lives in the api
 * Worker, which is where ADR 0002 put it.
 *
 * The CEREMONY IS NOT IN THIS TREE. It is a separate Vite entry (sign.html → src/sign.tsx),
 * because ux.md §1 budgets it at ≤ 120 KB gzipped against the dashboard's 250 KB — different
 * numbers for different audiences. A lazy route would still put React Router, the app shell and the
 * auth screens in the entry chunk a signer downloads before they can read a contract.
 */
import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import NotFound from './NotFound'
import { LoadingState } from '@flowza/ui'
import Login from './auth/Login'
import Signup from './auth/Signup'
import Reset, { RESET_CONFIRM_PATH } from './auth/Reset'
import ResetConfirm, { parseLanding } from './auth/ResetConfirm'
import Join from './auth/Join'
import Shell from './app/Shell'
import Home from './app/Home'
import { RouteError } from './RouteError'
import { RequireSession } from '../auth/session'
import { WorkflowProvider } from '../auth/workflow'
import { PermissionsProvider } from '../auth/permissions'
import Envelopes from './app/Envelopes'
import { PaymentsPlaceholder } from './app/admin/Placeholder'

// The editor is its own chunk: pdf.js and the coordinate library ride with it, not with the shell
// every dashboard visit downloads (ux.md §1 budgets; infra/verify-bundle-budget.mjs charges a route
// only for what it imports statically).
const Editor = lazy(() => import('./app/Editor'))

/**
 * The areas that are not the first screen, each in its own chunk.
 *
 * They used to be static imports, which put every one of them in the shared entry chunk — the one
 * the budget charges to EVERY surface, because a static import is part of the initial download
 * whoever is downloading it. So the field editor paid for the Admin security log, the Developer
 * area's API-key screen and the Templates list, none of which it can reach; and a visitor who
 * opened the dashboard downloaded all three before seeing it.
 *
 * That is how the editor's headroom went from 9% to 5% in a day: three areas were added and the
 * cost landed on a budget whose stated job is to be a tripwire for the fill engine leaking into the
 * browser (ADR 0008 §7). A tripwire with 5% of slack catches the next ordinary change instead.
 *
 * Home (the dashboard, or the International home's loading state), Shell and the auth screens
 * stay static on purpose. They ARE the first paint, and
 * splitting them would trade a real byte saving for a spinner on the one navigation that has no
 * previous screen to spin on. The two reset screens are auth screens: somebody who cannot sign in
 * is the last person to spend on a chunk fetch, and together they are a few hundred bytes.
 */
const Inbox = lazy(() => import('./app/Inbox'))
const Contacts = lazy(() => import('./app/Contacts'))
const Settings = lazy(() => import('./app/Settings'))
const StampsPage = lazy(() => import('./app/Stamps'))
const Reports = lazy(() => import('./app/Reports'))
const TemplatesPage = lazy(() => import('./app/Templates'))
// The three console pages that replaced placeholders in Prompt 5. Lazy like every other console
// area: a connector page nobody opens should not ride in the shell chunk.
const SubscriptionPage = lazy(() => import('./app/admin/Subscription'))
const CloudStoragesPage = lazy(() => import('./app/admin/CloudStorages'))
import { ORG_SECTIONS } from './app/admin/orgSections'

const SingleSignOnPage = lazy(() => import('./app/admin/SingleSignOn'))
// One chunk for all fifteen organization screens: they are one area, and a reader who opens
// Organization may well open Teams next. Their NAMES come from `orgSections.ts`, which is tiny
// and eager, so declaring these routes costs the entry bundle nothing.
const OrgSectionPage = lazy(() => import('./app/admin/OrgSectionPage'))
const ComplianceSettingsPage = lazy(() => import('./app/admin/ComplianceSettings'))
// Was `DocumentsPlaceholder` (ADR 0106): the team's folders and its two filing settings.
const AdminDocumentsPage = lazy(() => import('./app/admin/Documents'))
// The profile wizard an invitation lands on (Prompt 5).
const WelcomePage = lazy(() => import('./app/welcome/Welcome'))
const DeveloperPage = lazy(() => import('./app/Developer'))
/**
 * Connect (ADR 0092). Its own chunk and its own area: the webhook lane's management used to be a
 * panel inside Developer, which is the right shape for somebody holding an API key and the wrong
 * one for an administrator asking why an event did not arrive.
 */
const ConnectPage = lazy(() => import('./app/Connect'))
const AdminPage = lazy(() => import('./app/Admin'))
/**
 * The staff console (ADR 0177). Its own chunk, and the one most clearly worth splitting: a handful
 * of people will ever load it, and it must not ride in the entry chunk every signer and every
 * tenant downloads.
 */
const StaffConsole = lazy(() => import('./adm/StaffConsole'))
// The staff door. Its own chunk like the rest of the console, and never bundled with the tenant
// sign-in: a customer signing in should not be made to download the staff screens.
const StaffLogin = lazy(() => import('./adm/StaffLogin'))
// Its four screens, each its own chunk. A staff console is what a handful of people open and
// nobody else ever loads, so none of it belongs in the bundle a signer pays for.
const StaffDashboard = lazy(() => import('./adm/StaffDashboard'))
const StaffTenants = lazy(() => import('./adm/StaffTenants'))
const StaffTenantDetail = lazy(() => import('./adm/StaffTenantDetail'))
const StaffMemberLookup = lazy(() => import('./adm/StaffMemberLookup'))
/**
 * The admin console (ADR 0066).
 *
 * Its own chunk, like every other area: an admin console is what a handful of people in an
 * organization open occasionally, and putting its eight pages in the entry chunk would charge every
 * dashboard visit and every field-editor session for them. The five it splits into are loaded by
 * the same `chunked()` wrapper as the rest.
 */
const AdminConsole = lazy(() => import('./app/admin/AdminShell'))
const AllWorkspaces = lazy(() => import('./app/admin/AllWorkspaces'))
// The four PEOPLE screens are lazily imported by `Settings.tsx` now, not here (ADR 0121): they are
// sections of Settings › People, and the admin paths that used to render them redirect there.
const CustodyTransferPage = lazy(() => import('./app/admin/CustodyTransfer'))
const WorkspaceMembersPage = lazy(() => import('./app/admin/WorkspaceMembers'))
const WorkspaceSettingsPage = lazy(() => import('./app/admin/WorkspaceSettings'))
const ComposerPage = lazy(() => import('./app/Composer'))
const LibraryPage = lazy(() => import('./app/library/Library'))
const Documents = lazy(() => import('./app/Documents'))
const Recipients = lazy(() => import('./app/Recipients'))

/**
 * One wrapper, so a lazy route cannot be added without a fallback.
 *
 * Written as a function rather than a component so the route table keeps reading as a list of
 * paths: `element: chunked(<Settings />, 'settings')` says the same thing as the eager form did,
 * with the loading state that a split chunk requires.
 */
const chunked = (element: ReactNode, label: string) => (
  <Suspense fallback={<LoadingState label={`Loading ${label}`} />}>{element}</Suspense>
)

/**
 * Deployment diagnostics: AN OPERATOR'S PAGE, and deliberately not a tenant's.
 *
 * It used to be `/app/developer`, which put it in the left nav of every signed-in customer. What it
 * renders is the Supabase host this build talks to, the cell's project ref, and a live isolation
 * probe over internal table names — infrastructure detail no eSignature product shows the people who
 * buy it, and a security self-attestation that a tenant would watch turn red before we did. None of
 * it is a credential leak (the publishable key and URL ship in the bundle by construction, and RLS
 * is the boundary), which is exactly why it reads as harmless and is not.
 *
 * `/app/developer` is NOT this page. That area is the tenant developer portal — API keys, webhooks,
 * event log, sandbox switch (ux.md §2, §9) — and it lands in Phase 8. Until then the area is marked
 * "Soon" in the nav and falls through to the dashboard like every other unlanded area.
 *
 * `VITE_DIAGNOSTICS` is a string literal at build time, so an unset flag makes this `false` and
 * Rollup drops the branch WITH its dynamic import: the page is not merely unrouted in a tenant
 * build, it is not in the output at all. Set it to `1` for an operator build.
 */
const Diagnostics =
  import.meta.env.VITE_DIAGNOSTICS === '1' ? lazy(() => import('./app/Diagnostics')) : null

const DIAGNOSTICS_ROUTE = Diagnostics
  ? [{ path: 'diagnostics', element: chunked(<Diagnostics />, 'diagnostics') }]
  : []

/**
 * Exported so the tree can be inspected without a browser.
 *
 * `routes.test.tsx` asserts which routes are split, because the budget alone cannot: it fails only
 * once a surface is fully over, so a static import that adds 8 KB reads as "still passing" right up
 * until an unrelated change tips it. The test names the invariant instead of measuring its symptom.
 */
/**
 * The root, and the one place a stray recovery fragment can be rescued.
 *
 * `resetPasswordForEmail` asks GoTrue to send the browser to `/auth/reset/confirm`, and GoTrue
 * honours that only if the URL is in the project's Redirect URLs allow-list (the go-live runbook
 * sets it to `https://sign.<domain>/**`). An unlisted redirect is not refused — GoTrue silently
 * substitutes the Site URL, which is this path. The fragment survives that substitution, so the
 * tokens arrive here, where `Navigate` would drop them and land the caller on a dashboard they
 * cannot get into and a password they still cannot change. Nothing about that is visible in a log.
 *
 * So the fragment is read before anything consumes it and forwarded intact. Only `recovery` is
 * rescued: GoTrue's failure fragment carries no `type`, so an expired-link error here is
 * indistinguishable from any other flow's, and guessing would put "your reset link expired" in
 * front of somebody who never asked for one.
 */
function Root() {
  const hash = typeof window === 'undefined' ? '' : window.location.hash
  if (parseLanding(hash).kind === 'recovery') {
    return <Navigate to={{ pathname: RESET_CONFIRM_PATH, hash }} replace />
  }
  return <Navigate to="/app" replace />
}

export const routes = [
  { path: '/', element: <Root /> },
  { path: '/auth/login', element: <Login /> },
  { path: '/auth/signup', element: <Signup /> },
  // Login has linked to /auth/reset since it first had a "Forgotten your password?" line, and
  // nothing was registered here: the link left the SPA at a path with no match and rendered React
  // Router's default error page. `/auth/*` is a Pages rewrite to this app, so it answered 200 and
  // broke on arrival — invisible in any log. Password reset is MVP (implementation-plan.md 498).
  { path: '/auth/reset', element: <Reset /> },
  // Where the emailed link lands, with the recovery tokens in the fragment. OUTSIDE RequireSession:
  // somebody who cannot sign in is exactly who follows it.
  { path: '/auth/reset/confirm', element: <ResetConfirm /> },
  // Where an invitation link lands. OUTSIDE RequireSession, and it has to be: the whole point is
  // that the person following it does not have an account yet. The token rides in the fragment,
  // which never reaches this server or any log, and Join clears it from the address bar on read.
  { path: '/join', element: <Join /> },
  {
    /*
      The staff door, OUTSIDE `RequireSession` — and it has to be, for the same reason `/auth/login`
      is: somebody who is not signed in is exactly who arrives here. It is declared before `/adm`
      rather than as a child of it because it is not part of the console: it has no nav, it is not
      behind the session guard, and a `login` child of a guarded parent would be unreachable by the
      only people who need it.

      It grants nothing. Staff authority is `staffConsoleRefusal` in `@flowza/core` — an allowlist
      plus `aal2`, checked in every `/v1/staff/*` handler — so this route being public costs
      nothing: what it can do is ask, and show the answer.
    */
    path: '/adm/login',
    element: chunked(<StaffLogin />, 'the staff sign-in'),
    errorElement: <RouteError scope="app" />,
  },
  {
    // The staff console (ADR 0177), at the path the founder asked for.
    //
    // OUTSIDE the `/app` tree on purpose, and not merely for layout: that tree is an
    // ORGANIZATION's console — workspace switcher, org name, a member's navigation — and none of
    // it means anything to somebody looking at every tenant on the cell. Nesting it there would
    // also put `WorkflowProvider` behind it, polling a tenant endpoint on a screen that is not
    // about a tenant.
    //
    // `RequireSession` and nothing more: being signed in is the price of admission to the SCREEN,
    // never to the data. Who may actually read a tenant is decided by the API — an allowlist of
    // user ids plus a second factor — because "never rely on frontend-only authorization"
    // (CLAUDE.md §2 never 3) applies most sharply to the one console that can read every customer.
    // A non-staff visitor here gets a screen that loads and then says the console is unavailable,
    // which is the correct amount to tell them.
    path: '/adm',
    element: (
      <RequireSession>
        <Suspense fallback={<LoadingState label="Loading the staff console" />}>
          <StaffConsole />
        </Suspense>
      </RequireSession>
    ),
    errorElement: <RouteError scope="app" />,
    /*
      THE CONSOLE IS FIVE SCREENS NOW, AND THEY ARE REAL ROUTES (ADR 0186).

      It was one screen that swapped its own contents in state, so there was exactly one address
      for all of it: a staff member could not send a colleague "the suspended enterprise tenants"
      or "that tenant's security tail", and a browser Back out of a tenant left the console
      entirely. The source console this ports from addresses every section, and that is the half of
      its interface most worth copying — a support conversation is mostly people sending each other
      links.

      `tenants/:orgId` is a child rather than a modal for the same reason, and it costs nothing:
      the justification gate lives on that screen, so the address is linkable while the DATA behind
      it still is not readable without a typed reason. A link is an invitation to look, never a
      grant.
    */
    children: [
      /*
        `/adm` IS THE DOOR, and the dashboard moved to `/adm/dashboard` to make room for it.

        Founder's instruction: "/adm should give me directly the login admin page where I enter
        email and password and 2FA — it should take me to login always." Before this, `/adm` was
        the dashboard behind `RequireSession`, which meant only a SIGNED-OUT visitor was sent to
        the door. Anybody already signed in as an ordinary tenant account — which is the common
        case, because the staff console shares the tenant realm (ADR 0177) — passed the session
        guard and got the console shell with a refusal notice inside it. That is a screen that
        looks broken rather than a screen that asks who you are.
      */
      { index: true, element: <Navigate to="/adm/login" replace /> },
      /*
        The dashboard's own address. `/adm` used to be it, and ADR 0186's argument for addressable
        sections is exactly why it gets a real path rather than disappearing: a staff member sends
        a colleague a link. An old `/adm` bookmark still lands here — it redirects to the door, and
        the door forwards an already-admitted session on, which is one hop rather than a dead end.
      */
      { path: 'dashboard', element: chunked(<StaffDashboard />, 'the staff dashboard') },
      { path: 'tenants', element: chunked(<StaffTenants />, 'tenants') },
      { path: 'tenants/:orgId', element: chunked(<StaffTenantDetail />, 'the tenant') },
      { path: 'member-lookup', element: chunked(<StaffMemberLookup />, 'the member lookup') },
    ],
  },
  {
    // The console is for someone: no session, no shell (auth/session.tsx). What that someone may
    // see is decided by RLS and the API, never here.
    path: '/app',
    element: (
      // The workflow provider wraps the SHELL, not the app: it polls an authenticated endpoint, so
      // there is nothing for it to ask before somebody is signed in, and a provider outside
      // `RequireSession` would poll a 401 once a minute on the login page.
      <RequireSession>
        {/*
          The permissions provider sits inside `RequireSession` for the same reason: it reads
          `/v1/profile` once for the whole console (ADR 0196), and there is nobody to read it for
          before somebody is signed in.
        */}
        <PermissionsProvider>
          <WorkflowProvider>
            <Shell />
          </WorkflowProvider>
        </PermissionsProvider>
      </RequireSession>
    ),
    // Every screen in the console falls back here when it throws (routes/RouteError.tsx). Without
    // it React Router shows its own developer screen — a stack trace and "Hey developer 👋" — which
    // is what an administrator was shown when All Members crashed on a field the deployed Worker
    // omits.
    errorElement: <RouteError scope="app" />,
    children: [
      // The dashboard under FlowZa Sign; My Documents under the International standard (ADR
      // 0095). One route either way, so `/app` means "home" whichever an administrator sets.
      { index: true, element: <Home /> },
      ...DIAGNOSTICS_ROUTE,
      // Everything waiting on this person, from both directions. The first screen in the console
      // that shows a person what was sent TO them rather than only what they sent.
      { path: 'inbox', element: chunked(<Inbox />, 'your inbox') },
      // The address book, so a recipient is picked rather than retyped (ux.md §2).
      { path: 'contacts', element: chunked(<Contacts />, 'contacts') },
      // Two paths, one screen. The bare one keeps working — the sidebar links to it, and so does
      // every bookmark made before the rail existed — and renders the first section; the second
      // makes each section a place that can be linked, bookmarked and returned to with the back
      // button. An unknown slug falls back to the first section rather than 404ing, because a
      // typo in a settings URL should still show somebody their settings.
      { path: 'settings', element: chunked(<Settings />, 'settings') },
      /*
        SINGLE SIGN-ON IS THE ADMIN CONSOLE'S (ADR 0134). Founder, 2026-09-13: "Settings, Single
        sign-on and Admin 'Single sign-on (SSO)' is same, please move it to Admin itself. No need
        two locations, wire properly."

        A static segment outranks `:section` in React Router's ranking whatever the order here, so
        this catches `/app/settings/sso` before the rail does — and the rail no longer has an `sso`
        entry to catch it with. It REDIRECTS rather than 404s for the reason the four People paths
        do: this URL is linked from the console's own "what is where" panel and is the one an
        administrator who set SSO up last week has bookmarked.
      */
      { path: 'settings/sso', element: <Navigate to="/app/admin/sso" replace /> },
      { path: 'settings/:section', element: chunked(<Settings />, 'settings') },
      // The shelf of seals (ADR 0082): reached from Settings, and its own page because it is one.
      { path: 'stamps', element: chunked(<StampsPage />, 'stamps') },
      // Volume, completion rate and time-to-sign. NOT per-team — this build has no teams, and the
      // screen says so rather than showing an empty panel.
      { path: 'reports', element: chunked(<Reports />, 'reports') },
      // Reuse a layout when you send. NOT public links and NOT bulk send — the other two things
      // ux.md line 62 puts in this area — and the screen says so at the top rather than leaving a
      // customer to find out by looking for them.
      { path: 'templates', element: chunked(<TemplatesPage />, 'templates') },
      // API keys. NOT webhooks, logs, docs or test mode — the other four things ux.md line 62 puts
      // in this area — and the screen names all four at the top, with test mode's reason spelled
      // out because it is not merely unbuilt.
      { path: 'developer', element: chunked(<DeveloperPage />, 'the developer area') },
      // Where the organization's systems hear about it (ADR 0092): endpoints, the delivery log,
      // what failed anywhere, a test delivery and a replay. Moved out of Developer, which now
      // points here rather than keeping a second copy of the same panel.
      { path: 'connect', element: chunked(<ConnectPage />, 'Connect') },
      // Registered while the nav still says "Soon", and the two are not in conflict. The label is a
      // claim about the AREA — ux.md line 66 lists nine things and one is built — while the route is
      // what a link from the security-policy screen needs to land on. Before this, clicking "Admin"
      // fell through to the dashboard, which explained nothing.
      /**
       * The admin console, a nested area.
       *
       * `/app/admin` indexes to All Workspaces, and the SECURITY LOG — which used to be the whole
       * of this route — moves to `/app/admin/security-log`. That is a deliberate move of a URL that
       * worked: the log is one of nine things `ux.md` line 66 puts in this area, and leaving it at
       * the root would have made "Admin" mean "the security log" forever. `routes.test.tsx` and
       * `dead-links.test.ts` are updated with it.
       */
      {
        path: 'admin',
        element: chunked(<AdminConsole />, 'admin settings'),
        // Its OWN boundary, inside the console's: React Router walks up to the nearest one, so a
        // panel that throws here leaves the admin nav on screen and the reader can go elsewhere
        // without reloading.
        errorElement: <RouteError scope="admin" />,
        children: [
          { index: true, element: chunked(<AllWorkspaces />, 'workspaces') },
          /*
            THE FIFTEEN THAT MOVED IN (ADR 0156), generated from `ORG_SECTIONS` rather than
            typed out here.

            One array feeds this and the console's nav, so a screen cannot reach the router and
            miss the nav — unreachable while every test stays green, which is the failure the rail
            version of this list was written to catch.

            FOUR OF THESE PATHS USED TO REDIRECT THE OTHER WAY. `/app/admin/members`, `/users`,
            `/groups` and `/permission-profiles` pointed at `/app/settings/*` under ADR 0121, and
            Settings now points here — so leaving those redirects in place would have been an
            infinite loop rather than a stale link. They are gone, replaced by the screens
            themselves, which is what makes the reversal safe.
          */
          ...ORG_SECTIONS.map((section) => ({
            path: section.slug,
            element: chunked(<OrgSectionPage slug={section.slug} />, section.label.toLowerCase()),
          })),
          {
            // Who holds what, and the rules that move it (ADR 0085). Organization-level, like
            // members: custody is a fact about a person and a document, never about a team.
            path: 'custody',
            element: chunked(<CustodyTransferPage />, 'custody transfer'),
          },
          { path: 'security-log', element: chunked(<AdminPage />, 'the security log') },
          {
            // Was `SubscriptionPlaceholder`. The placeholder said "there is no billing system yet"
            // and that is still true — what changed is that the plan, the seats, the trial runway
            // and the meter ARE knowable, and a page that shows them is not a promise of billing.
            path: 'subscription',
            element: chunked(<SubscriptionPage />, 'your subscription'),
          },
          /*
            WORKSPACE MEMBERS AND BRANDING ARE OFF THE NAV (ADR 0134). Founder, 2026-09-13: "Admin,
            'Workspace members' & 'Branding and Customization' should be removed, as already
            available in the Settings, Members … branding and customization already there as Brand
            in settings, both are same."

            They part company at the ROUTE, though, because only one of them was really a duplicate:

            - Branding at team level was a PLACEHOLDER whose own sentence said branding is set for
              the whole organization and lives in Settings. A screen that exists to say it is not
              that screen is the second location the founder is describing, so it redirects to the
              real one and `BrandingPlaceholder` is gone.
            - Workspace members is a REAL per-team list and Settings › Members is the organization's.
              It stays reachable, because All Workspaces' own "Members" action opens it and a team's
              membership is not shown anywhere else. What it loses is the standing nav row, which is
              what the ask was: a drill-down from a team row, not a section of the console.
          */
          {
            path: 'workspaces/:workspaceId/members',
            element: chunked(<WorkspaceMembersPage />, 'the team'),
          },
          {
            path: 'workspaces/:workspaceId/settings',
            element: chunked(<WorkspaceSettingsPage />, 'the team’s settings'),
          },
          {
            path: 'workspaces/:workspaceId/branding',
            element: <Navigate to="/app/admin/brand" replace />,
          },
          { path: 'workspaces/:workspaceId/payments', element: <PaymentsPlaceholder /> },
          {
            // Was `CompliancePlaceholder`, whose sentence was "per-team compliance lands with the
            // workspace-settings release". It landed (ADR 0072), so the placeholder is now false.
            path: 'workspaces/:workspaceId/compliance',
            element: chunked(<ComplianceSettingsPage />, 'compliance settings'),
          },
          {
            path: 'workspaces/:workspaceId/documents',
            element: chunked(<AdminDocumentsPage />, 'the team’s documents'),
          },
          {
            // Was `StoragePlaceholder`, whose sentence was "no connector is available yet".
            // Four are, so the placeholder would now be false (D7, ADR 0070).
            path: 'workspaces/:workspaceId/storage',
            element: chunked(<CloudStoragesPage />, 'cloud storage'),
          },
          // The same page without a team in the URL, because a connector is usually organization-wide
          // and the callback has to redirect somewhere that does not invent a workspace id.
          { path: 'storage', element: chunked(<CloudStoragesPage />, 'cloud storage') },
          {
            path: 'workspaces/:workspaceId/sso',
            element: chunked(<SingleSignOnPage />, 'single sign-on'),
          },
          // The same page without a team in the URL, for the reason `storage` has one and with a
          // sharper edge: a connection is ONE PER ORGANIZATION (`uq_org_sso_connections__org`), so
          // a team in the path is not merely unnecessary, it implies a per-team setting that does
          // not exist. The Microsoft consent callback also has to send the administrator back
          // somewhere that does not invent a workspace id.
          { path: 'sso', element: chunked(<SingleSignOnPage />, 'single sign-on') },
        ],
      },
      /**
       * The profile wizard an accepted invitation lands on (Prompt 5).
       *
       * Inside the app shell rather than beside it: somebody completing their profile is signed in
       * and should still have the nav, because the wizard is skippable at every step and a person
       * who skips it needs somewhere to go.
       */
      { path: 'welcome', element: chunked(<WelcomePage />, 'your profile') },
      // The composer's document panel (Phase 3): upload, watch the conversion, replace the file.
      { path: 'documents', element: <Envelopes /> },
      // The INTERNATIONAL composer (founder decision D8). `new` starts a draft lazily — nothing is
      // written until the first file is dropped — and `setup` resumes one. Two paths rather than an
      // optional parameter, because "no envelope yet" and "this envelope" are genuinely different
      // states and a route that conflated them would create a second draft on a reload.
      { path: 'envelopes/new', element: chunked(<ComposerPage />, 'the composer') },
      // The library's three shelves (founder decisions D3 and D9). ONE screen with a shelf prop
      // rather than three components: they differ in which filter they send and nothing else, and
      // three copies would be three places to fix the next thing that is wrong with a list.
      { path: 'archive', element: chunked(<LibraryPage shelf="archived" />, 'your archive') },
      { path: 'trash', element: chunked(<LibraryPage shelf="trashed" />, 'the bin') },
      { path: 'library', element: chunked(<LibraryPage shelf="active" />, 'your documents') },
      // THE ENVELOPE ITSELF. `/app/envelopes/{id}` matched nothing, so it fell to the `*` below and
      // landed on the Dashboard with no message at all — a link somebody pasted, or the address a
      // person builds by deleting `/recipients` off the end, took them somewhere else in silence
      // (the testing team, 2026-09-16, BUG-16).
      //
      // It resolves to the recipients step, which is the one screen that answers for an envelope in
      // BOTH states: on a draft it is step three with the send button, and on a sent one it is the
      // status list the report was looking at. An id that does not exist is not swallowed either —
      // that screen says "Recipients could not load" with the API's own distinction between a row
      // that is gone and a route this deployment does not serve.
      { path: 'envelopes/:envelopeId', element: <Navigate to="recipients" replace /> },
      { path: 'envelopes/:envelopeId/setup', element: chunked(<ComposerPage />, 'the composer') },
      { path: 'envelopes/:envelopeId/documents', element: chunked(<Documents />, 'documents') },
      // The recipients step and the send. Until this route existed the API could add a recipient
      // and send an envelope and the dashboard could do neither.
      { path: 'envelopes/:envelopeId/recipients', element: chunked(<Recipients />, 'recipients') },
      {
        path: 'envelopes/:envelopeId/documents/:documentId/place',
        element: chunked(<Editor />, 'the editor'),
      },
      // The remaining six areas land with the features that fill them. A nav entry pointing at a
      // route that does not exist is a dead end, so they resolve to the dashboard until then.
      { path: '*', element: <Navigate to="/app" replace /> },
    ],
  },
  {
    // THE TOP-LEVEL CATCH-ALL, and until 2026-09-15 there was not one. Inside `/app` an unknown
    // path resolves to the dashboard (above), but anything that did not match a top-level route at
    // all fell through to React Router's development screen: "Unexpected Application Error!",
    // "404 Not Found", and "Hey developer 👋" with advice about adding an `ErrorBoundary` prop.
    //
    // Found in production by typing `/login` — the reasonable guess when the real path is
    // `/auth/login` — and again on `/pricing`. Both are addresses a stranger reaches by guessing or
    // from a stale link, so the developer screen was the first thing some people saw of this
    // product. It must be last: React Router matches in order and `*` matches everything.
    path: '*',
    element: <NotFound />,
  },
]

const router = createBrowserRouter(routes)

export default function Routes() {
  return <RouterProvider router={router} />
}
